import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { useCustomer } from '../contexts/customer-context';
import { 
  Container, 
  Typography, 
  TextField, 
  Button, 
  Box,
  Paper,
  Fade,
  CircularProgress,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import CelebrationIcon from '@mui/icons-material/Celebration';
import { fetchBusinessData, checkUserReviews, checkUserAccount, createAccount, loginUser } from '../utils/api';
import CheckIcon from '@mui/icons-material/Check';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import RateReviewIcon from '@mui/icons-material/RateReview';

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  boxShadow: '0 8px 20px rgba(0, 0, 0, 0.15)',
  borderRadius: '16px',
  background: 'linear-gradient(145deg, #ffffff, #f0f0f0)',
  transition: 'all 0.3s ease-in-out',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(3),
  },
}));

const Logo = styled('img')(({ theme }) => ({
  width: '150px',
  marginBottom: '20px',
  [theme.breakpoints.down('sm')]: {
    width: '120px',
    marginBottom: '15px',
  },
}));

const MilestoneBox = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(3),
  margin: theme.spacing(2, 0),
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  width: '100%',
  boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
}));

const CelebrationIconStyled = styled(CelebrationIcon)(({ theme }) => ({
  fontSize: '2.5rem',
  marginBottom: theme.spacing(1),
  color: theme.palette.secondary.main,
  animation: 'sparkle 1.5s infinite',
  '@keyframes sparkle': {
    '0%': { transform: 'scale(1) rotate(0deg)' },
    '50%': { transform: 'scale(1.1) rotate(5deg)' },
    '100%': { transform: 'scale(1) rotate(0deg)' }
  }
}));

const ErrorText = styled(Typography)(({ theme }) => ({
  color: theme.palette.error.main,
  fontSize: '0.875rem',
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(1),
}));

const ReviewBox = styled(Box)(({ theme }) => ({
  backgroundColor: 'rgba(255, 255, 255, 0.9)',
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadius,
  width: '100%',
  marginBottom: theme.spacing(2),
}));

const ScrollableReviewText = styled(Box)(({ theme }) => ({
  backgroundColor: 'white',
  padding: theme.spacing(1.5),
  borderRadius: theme.shape.borderRadius,
  maxHeight: '100px', // Fixed height
  overflowY: 'auto', // Enable vertical scrolling
  // Custom scrollbar styling
  '&::-webkit-scrollbar': {
    width: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: '#f1f1f1',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: '#888',
    borderRadius: '4px',
    '&:hover': {
      background: '#666',
    },
  },
}));

const PerksFormContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  maxWidth: '400px',
  margin: '0 auto',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
}));

const FeedbackThankYou = () => {
  // First, initialize all hooks
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { updateCustomerId, updateCustomerEmail } = useCustomer();

  // Then, destructure location.state
  const { 
    businessName, 
    totalReviews, 
    shouldPromptGoogleReview, 
    googleReviewUrl,
    reviewSourceId,
    review,
    businessId
  } = location.state || {};

  // Now declare all state variables
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [email, setEmail] = useState(location.state?.email || '');
  const [businessData, setBusinessData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userReviews, setUserReviews] = useState(null);
  const [hasAccount, setHasAccount] = useState(false);
  const [isCreatingAccount, setIsCreatingAccount] = useState(false);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [accountCreated, setAccountCreated] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState('');
  const [formError, setFormError] = useState('');
  const [generalError, setGeneralError] = useState('');
  const [showGooglePrompt, setShowGooglePrompt] = useState(shouldPromptGoogleReview || false);
  const [showPerksSection, setShowPerksSection] = useState(false);
  const [reviewCopied, setReviewCopied] = useState(false);

  useEffect(() => {
    const loadData = async () => {

      console.log('totalReviews:', totalReviews);

      if (businessId) {
        try {
          const businessData = await fetchBusinessData(businessId);
          
          if (!businessData) {
            throw new Error('Failed to fetch business data');
          }
          
          setBusinessData(businessData);

          console.log('businessData:', businessData);
          
          const emailFromState = location.state?.email;
          if (emailFromState) {
            setEmail(emailFromState);
            const [reviewsData, accountData] = await Promise.all([
              checkUserReviews(emailFromState, businessId),
              checkUserAccount(emailFromState)
            ]);
            setUserReviews(reviewsData);
            setHasAccount(accountData.hasAccount);
          } else {
            setHasAccount(false);
          }
        } catch (error) {
          console.error('Error loading data:', error);
          setPasswordError('An error occurred while loading data. Please try again later.');
        } finally {
          setLoading(false);
        }
      } else {
        setPasswordError('Business ID is missing');
        setLoading(false);
      }
    };
    loadData();
  }, [businessId, location.state?.email]);

  useEffect(() => {
    if (shouldPromptGoogleReview) {
      setShowGooglePrompt(true);
      console.log('Review content:', review);
    }
  }, [shouldPromptGoogleReview, review]);

  // Email validation function
  const validateEmail = (email) => {
    const re = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return re.test(String(email).toLowerCase());
  };

  // Password validation for account creation
  const validateCreatePassword = (password) => {
    const minLength = 6;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumbers = /\d/.test(password);
    //const hasNonalphas = /\W/.test(password);
    return password.length >= minLength && hasUpperCase && hasLowerCase && hasNumbers;
  };

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    setEmailError('');
  };

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
    setPasswordError('');
  };

  const handleConfirmPasswordChange = (e) => {
    setConfirmPassword(e.target.value);
    setConfirmPasswordError('');
  };

  const handleSignIn = () => {
    // Navigate to the login page with the email as a state parameter
    navigate('/login', { state: { email } });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setEmailError('');
    setPasswordError('');
    setConfirmPasswordError('');
    setGeneralError('');

    if (!validateEmail(email)) {
      setEmailError('Please enter a valid email address');
      setLoading(false);
      return;
    }

    if (!hasAccount && !accountCreated) {
      // Account creation logic
      if (!validateCreatePassword(password)) {
        console.log(password);
        setGeneralError('Password must be at least 6 characters long and include uppercase, lowercase and number');
        setLoading(false);
        return;
      }

      if (password !== confirmPassword) {
        setGeneralError("Passwords don't match");
        setLoading(false);
        return;
      }

      setIsCreatingAccount(true);
      try {
        const response = await createAccount(email, password);
        console.log('Account created', response);
        if (response.userId) {
          updateCustomerId(response.userId);
          updateCustomerEmail(email);
          setAccountCreated(true);
          setHasAccount(true);
        } else {
          console.log('User already exists:', response.message);
          setGeneralError(response.message || 'Failed to create account. Please try again.');
        }
      } catch (error) {
        console.error('Account creation error:', error);
        setGeneralError('An unexpected error occurred. Please try again.');
      } finally {
        setIsCreatingAccount(false);
        setLoading(false);
      }
    }
  };

  const generateAchievementMessage = (businessReviews) => {
    let message = `Congratulations! You've just submitted your `;
    
    if (businessReviews === 1 || businessReviews === 0) {
      message += `first review for ${businessData.businessName}! 🌟`;
    } else {
      message += `${getOrdinal(businessReviews)} review for ${businessData.businessName}! 🏆`;
    }

    return message;
  };

  const generateMilestoneMessage = (totalReviews) => {
    if (totalReviews === 5 || totalReviews === 10 || totalReviews === 25 || totalReviews === 50 || totalReviews === 100) {
      return `You've reached a milestone of ${totalReviews} total reviews!`;
    }
    return null;
  };

  const generateRegularMessage = (totalReviews) => {
    return `You've now submitted ${totalReviews} overall ratings!`;
  };

  const getOrdinal = (n) => {
    const s = ["th", "st", "nd", "rd"];
    const v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
  };

  const generateEncouragementMessage = (ratingForNextMilestone) => {
    if (ratingForNextMilestone < 5 && ratingForNextMilestone > 0) {
      return `You're just ${ratingForNextMilestone} reviews away from your next milestone! Keep sharing your experiences and helping others make informed decisions.`;
    } else {
      return ``;
    }
  };

  const AchievementSection = () => (
    <Box sx={{ mt: 2, mb: 3, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <CelebrationIconStyled />
      <Typography variant="body1" align="center" sx={{ fontWeight: 600, color: 'primary.main', mb: 2 }}>
        {generateAchievementMessage(totalReviews)}
      </Typography>
      {generateMilestoneMessage(totalReviews) && (
        <Typography variant="body2" align="center" sx={{ color: 'text.secondary' }}>
          {generateMilestoneMessage(totalReviews)}
        </Typography>
      )}
    </Box>
  );

  const handleCopyReview = async () => {
    try {
      await navigator.clipboard.writeText(review);
      setReviewCopied(true);
      setTimeout(() => setReviewCopied(false), 2000);
      console.log('Review copied to clipboard');
    } catch (err) {
      console.error('Failed to copy review:', err);
    }
  };

  const GoogleReviewPrompt = () => (
    <Fade in={showGooglePrompt}>
      <MilestoneBox>
        <Typography variant="h6" align="center" gutterBottom>
          Love our service? Share it on Google!
        </Typography>
        
        {/* Review Section */}
        {review && (
          <ReviewBox>
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              mb: 1 
            }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: 'primary.main',
                  fontWeight: 500
                }}
              >
                Your review:
              </Typography>
              <Button
                size="small"
                onClick={handleCopyReview}
                startIcon={reviewCopied ? <CheckIcon /> : <ContentCopyIcon />}
                sx={{ 
                  minWidth: 'auto',
                  color: reviewCopied ? 'success.main' : 'primary.main',
                  p: 0.5,
                  '&:hover': {
                    backgroundColor: 'transparent',
                  }
                }}
              >
                {reviewCopied ? 'Copied' : 'Copy'}
              </Button>
            </Box>
            <ScrollableReviewText>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: 'text.primary',
                  lineHeight: 1.5
                }}
              >
                {review}
              </Typography>
            </ScrollableReviewText>
          </ReviewBox>
        )}

        <Button
          variant="contained"
          onClick={() => {
            console.log(`Google review click - Source ID: ${reviewSourceId}`);
            window.open(googleReviewUrl, '_blank');
            setShowPerksSection(true);
          }}
          startIcon={<RateReviewIcon />}
          fullWidth
          sx={{ 
            py: 1.5,
            fontWeight: 600,
            backgroundColor: 'white',
            color: 'primary.main',
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.9)'
            }
          }}
        >
          Write a Google Review
        </Button>

        {review && (
          <Typography 
            variant="body2" 
            align="center" 
            sx={{ 
              color: 'white',
              mt: 1,
              fontSize: '0.85rem'
            }}
          >
            Tip: Copy your review above and paste it on Google
          </Typography>
        )}
      </MilestoneBox>
    </Fade>
  );

  const PerksSection = () => (
    <Fade in={showPerksSection || !shouldPromptGoogleReview}>
      <Box sx={{ mt: 4, width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Typography variant="h5" gutterBottom align="center" 
          sx={{ fontWeight: 600, color: 'secondary.main', fontSize: isMobile ? '1.3rem' : '1.5rem' }}>
          {accountCreated ? 'Account Created!' : (hasAccount ? 'Welcome Back!' : 'Unlock Exclusive Perks!')}
        </Typography>
        
        <Typography variant="body2" align="center" 
          sx={{ mb: 3, color: 'text.secondary', fontSize: isMobile ? '0.8rem' : '0.875rem' }}>
          {accountCreated 
            ? "Your account has been created successfully. Please log in to access your profile and rewards."
            : (hasAccount 
              ? "Ready to see your rewards? Log in to your account and discover exclusive offers tailored just for you!" 
              : "Your feedback is valuable! Create an account now to access special offers, track your review milestones, and enjoy personalized perks.")}
        </Typography>

        <PerksFormContainer>
          {hasAccount || accountCreated ? (
            <Button 
              onClick={handleSignIn}
              variant="contained" 
              color="primary" 
              fullWidth 
              sx={{ 
                py: isMobile ? 1 : 1.5, 
                fontWeight: 600, 
                fontSize: isMobile ? '0.9rem' : '1.1rem', 
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)', 
                '&:hover': { 
                  transform: 'translateY(-2px)', 
                  boxShadow: '0 6px 8px rgba(0, 0, 0, 0.15)' 
                } 
              }}
              size={isMobile ? "medium" : "large"}
            >
              Sign In
            </Button>
          ) : (
            <form onSubmit={handleSubmit} style={{ width: '100%' }}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={email}
                onChange={handleEmailChange}
                required
                margin="normal"
                variant="outlined"
                error={!!emailError}
                helperText={emailError}
                sx={{ '& .MuiOutlinedInput-root': { '&.Mui-focused fieldset': { borderColor: 'secondary.main' } } }}
              />
              <TextField
                fullWidth
                label="Password"
                type="password"
                value={password}
                onChange={handlePasswordChange}
                required
                margin="normal"
                variant="outlined"
                sx={{ '& .MuiOutlinedInput-root': { '&.Mui-focused fieldset': { borderColor: 'secondary.main' } } }}
              />
              <TextField
                fullWidth
                label="Confirm Password"
                type="password"
                value={confirmPassword}
                onChange={handleConfirmPasswordChange}
                required
                margin="normal"
                variant="outlined"
                sx={{ '& .MuiOutlinedInput-root': { '&.Mui-focused fieldset': { borderColor: 'secondary.main' } } }}
              />
              {generalError && <ErrorText>{generalError}</ErrorText>}
              <Button 
                type="submit" 
                variant="contained" 
                color="primary" 
                fullWidth 
                disabled={isCreatingAccount}
                sx={{ 
                  mt: 3, 
                  py: isMobile ? 1 : 1.5, 
                  fontWeight: 600, 
                  fontSize: isMobile ? '0.9rem' : '1.1rem', 
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)', 
                  '&:hover': { 
                    transform: 'translateY(-2px)', 
                    boxShadow: '0 6px 8px rgba(0, 0, 0, 0.15)' 
                  } 
                }}
                size={isMobile ? "medium" : "large"}
              >
                {isCreatingAccount ? 'Creating Account...' : 'Create Account & Get Perks'}
              </Button>
            </form>
          )}
        </PerksFormContainer>
      </Box>
    </Fade>
  );

  // Add loading check at the beginning of the render
  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ mt: 4, mb: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (!businessData) {
    return (
      <Container maxWidth="sm" sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h6" align="center">
          Error: Unable to load business data. Please try again later.
        </Typography>
      </Container>
    );
  }

  // Main render with safe access to businessData
  return (
    <Container maxWidth="sm" sx={{ mt: 4, mb: 4, px: isMobile ? 2 : 3 }}>
      <Fade in={true} timeout={1000}>
        <StyledPaper elevation={3}>
          {/* Safe access to logo URL */}
          {businessData?.logoUrl && (
            <Logo src={businessData.logoUrl} alt={`${businessData.businessName} Logo`} />
          )}
          
          <Typography variant="h4" component="h1" gutterBottom align="center" 
            sx={{ fontWeight: 700, color: 'primary.main', fontSize: isMobile ? '1.8rem' : '2.125rem' }}>
            Thank You for Your Feedback!
          </Typography>
          
          {/* Always show Achievement Section if totalReviews exists */}
          {totalReviews !== undefined && <AchievementSection />}
          
          {/* Google Review Section */}
          {shouldPromptGoogleReview && <GoogleReviewPrompt />}
          
          {/* Show button to reveal perks section if hidden */}
          {shouldPromptGoogleReview && !showPerksSection && (
            <Button
              onClick={() => setShowPerksSection(true)}
              variant="text"
              color="primary"
              sx={{ mt: 2 }}
            >
              Skip & Discover Your Exclusive Perks!
            </Button>
          )}
          
          {/* Perks Section */}
          {(!shouldPromptGoogleReview || showPerksSection) && <PerksSection />}
        </StyledPaper>
      </Fade>
    </Container>
  );
};

export default FeedbackThankYou;